import {
  withStyles,
  chooseColor,
  Term,
  Icon,
  InteracWordmark,
  Box,
  ConvenienceFeeRestriction,
  ELinxConnectionMethod,
  MaterialSymbol,
} from 'vopay';
import { FunctionComponent, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useLayout } from '@/providers/Layout.provider';
import { useToken } from '@/providers/Token.provider';
import { usePaylink, Flow } from '@/providers/Paylink.provider';
import GooglePayButton from '@/components/Google/GooglePayButton';
import styles from './ConnectionMethod.module.scss';
import PageTitle from '@/components/UI/PageTitle';
import Logos from '@/components/UI/Logos';
import { useConvenienceFeesProvider } from '@/providers/ConvenienceFees.provider';
import RestrictionsTooltip from '@/components/UI/RestrictionsTooltip';
import { ConvenienceFeeBreakdown, AcceptedCardBrandsTooltip } from 'UI';
import ListItem from './ListItem/ListItem';
import { useBank } from '@/providers/Bank.provider';
import { getBankingRailOptions, BankingRailOption } from '@/utils/bankingRailMapping';
import accountBalanceIcon from './Icons/accountBalance.svg';
import creditCardIcon from './Icons/creditCard.svg';

type LocationProp = {
  paymentMethod: string;
  origin: string;
};

const classes = withStyles(styles);

const ConnectionMethod: FunctionComponent = () => {
  const history = useHistory();
  const { setLayoutInfo } = useLayout();
  const { state } = useLocation<LocationProp>();
  const { token, isSetByBrowser } = useToken();
  const { flow, paylink } = usePaylink();
  const { setBankAccountRail } = useBank();
  const { t } = useTranslation('translations', { keyPrefix: 'PreferredMethod' });
  const { t: tConvenience } = useTranslation('translations', { keyPrefix: 'ConvenienceFee' });
  const { showFees, convenienceFees, getProcessingFee } = useConvenienceFeesProvider();

  // Get bank rail options if there are multiple rails available
  const bankRailOptions = useMemo(() => {
    if (!paylink.BankAccountRail || !Array.isArray(paylink.BankAccountRail)) {
      return [];
    }
    return getBankingRailOptions(paylink.BankAccountRail);
  }, [paylink.BankAccountRail]);

  const showBankRailSelection = useMemo(() => {
    return isEnabled('bank') && bankRailOptions.length > 1;
  }, [bankRailOptions]);

  const regionType = useMemo(() => {
    const isCanada = token?.Country?.toLowerCase() === 'canada' || token?.Country?.toLowerCase() === 'ca';

    return isCanada
      ? tConvenience('someProvinces', { defaultValue: 'some provinces' })
      : tConvenience('someStates', { defaultValue: 'some states' });
  }, [token?.Country]);

  const hasRestrictionsForCountry = useMemo(() => {
    if (!convenienceFees?.Restrictions || !token?.Country) return false;

    return convenienceFees.Restrictions.some(
      (restriction: ConvenienceFeeRestriction) =>
        restriction.CountryAbbreviation?.toLowerCase() === token.Country?.toLowerCase(),
    );
  }, [convenienceFees?.Restrictions, token?.Country]);

  function isPaymentMethodFree(paymentMethod: ELinxConnectionMethod) {
    const breakdown = getProcessingFee({
      paymentMethod,
    });

    if (!breakdown) return true;

    return !breakdown.hasFees;
  }

  useEffect(() => {
    setLayoutInfo(
      chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
      'sun',
      '',
      70,
      32,
      0,
      !isSetByBrowser,
      true,
    );
  }, []);

  function isEnabled(method: string) {
    return String(state?.paymentMethod) === 'any' || String(state?.paymentMethod).includes(method);
  }

  // Check if we should show combined credit/debit card option
  const shouldShowCombinedCard = useMemo(() => {
    return isEnabled('credit') && isEnabled('debitcard') && token?.CardTypeValidation === false;
  }, [state?.paymentMethod, token?.CardTypeValidation]);
  const handleBankRailSelection = (option: BankingRailOption) => {
    // Store the selected rail
    setBankAccountRail(option.rail);
    // Navigate to bank connection
    history.push('/connect/processor/bank');
  };

  const handleSingleBankSelection = () => {
    // Store the single bank rail
    setBankAccountRail(paylink?.BankAccountRail[0]);
    // Navigate to bank connection
    history.push('/connect/processor/bank');
  };

  return (
    <div className={classes('connectionMethodContainer')}>
      <div className={classes('main')}>
        <PageTitle title={`${t('methodTitle')}`} size="lg" bold />

        <div className={classes('connectionMethods')}>
          {/* Show bank rail options if there are multiple rails */}
          {showBankRailSelection ? (
            <div className={classes('bankAccountCard')}>
              <div className={classes('bankAccountHeader')}>
                <img src={accountBalanceIcon} alt="Bank" width="24px" height="24px" />
                <span className={classes('bankAccountTitle', 'paymentMethodHeader')}>{t('bankButton')}</span>
              </div>
              <div className={classes('bankRailOptions')}>
                {bankRailOptions.map((option) => (
                  <button
                    key={option.rail}
                    type="button"
                    className={classes('bankRailOptionItem')}
                    onClick={() => handleBankRailSelection(option)}
                  >
                    <div className={classes('bankRailContent')}>
                      <div className={classes('bankRailMainInfo')}>
                        <span className={classes('bankRailLabel')}>
                          {option.label}
                          {/* <Tooltip
                            icon="info"
                            variant="outlined"
                            iconWidth="8px"
                            buttonWidth="14px"
                            position="top"
                            information={option.tooltip}
                            hasArrow
                            width="280px"
                          /> */}
                        </span>
                      </div>
                      <div className={classes('bankRailSubInfo')}>
                        <ConvenienceFeeBreakdown connectionMethod="bank" bankingPaymentRail={option.rail} />
                      </div>
                    </div>
                    <MaterialSymbol icon="keyboard_arrow_right" size="24px" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Show single bank option if only one rail or bank not enabled */
            isEnabled('bank') && (
              <ListItem
                onClick={handleSingleBankSelection}
                icon={<img src={accountBalanceIcon} alt="Bank" width="24px" height="24px" />}
                content={
                  <>
                    <span className={classes('paymentMethodHeader')}>{t('bankButton')}</span>
                    <div className={classes('feeDisplay')}>
                      <ConvenienceFeeBreakdown
                        connectionMethod="bank"
                        bankingPaymentRail={paylink?.BankAccountRail[0]}
                      />
                    </div>
                  </>
                }
              />
            )
          )}

          {shouldShowCombinedCard ? (
            <ListItem
              to={{ pathname: '/connect/processor/credit', state: { origin: state?.origin } }}
              icon={<img src={creditCardIcon} alt="Card" width="24px" height="24px" />}
              content={
                <>
                  <Box className={classes('paymentMethodHeader')} ai="center" gapSize="4px">
                    <span>{t('creditDebitCardButton')}</span>
                    <AcceptedCardBrandsTooltip acceptedCardBrands={token?.AcceptedCardBrands} position="topRight" />
                  </Box>

                  <div className={classes('feeDisplay')}>
                    <ConvenienceFeeBreakdown connectionMethod="credit" />
                  </div>

                  {showFees && hasRestrictionsForCountry && !isPaymentMethodFree('credit') && (
                    <Box ai="center" gapSize="4px">
                      <span className={classes('restrictionText')}>
                        {tConvenience('feeWaivedInSome', {
                          regionType,
                          defaultValue: `This fee might be waived in some ${regionType}`,
                        })}
                      </span>

                      <RestrictionsTooltip
                        restrictions={convenienceFees?.Restrictions || []}
                        country={token?.Country}
                        position="right"
                      />
                    </Box>
                  )}
                </>
              }
            />
          ) : (
            <>
              {isEnabled('credit') && (
                <ListItem
                  to={{ pathname: '/connect/processor/credit', state: { origin: state?.origin } }}
                  icon={<img src={creditCardIcon} alt="Credit Card" width="24px" height="24px" />}
                  content={
                    <>
                      <Box className={classes('paymentMethodHeader')} ai="center" gapSize="4px">
                        <span>{t('creditCardButton')}</span>
                        <AcceptedCardBrandsTooltip acceptedCardBrands={token?.AcceptedCardBrands} position="topRight" />
                      </Box>

                      <div className={classes('feeDisplay')}>
                        <ConvenienceFeeBreakdown connectionMethod="credit" />
                      </div>

                      {showFees && hasRestrictionsForCountry && !isPaymentMethodFree('credit') && (
                        <Box ai="center" gapSize="4px">
                          <span className={classes('restrictionText')}>
                            {tConvenience('feeWaivedInSome', {
                              regionType,
                              defaultValue: `This fee might be waived in some ${regionType}`,
                            })}
                          </span>

                          <RestrictionsTooltip
                            restrictions={convenienceFees?.Restrictions || []}
                            country={token?.Country}
                            position="right"
                          />
                        </Box>
                      )}
                    </>
                  }
                />
              )}

              {isEnabled('debitcard') && (
                <ListItem
                  to={{ pathname: '/connect/processor/debitcard', state: { origin: state?.origin } }}
                  icon={<img src={creditCardIcon} alt="Debit Card" width="24px" height="24px" />}
                  content={
                    <>
                      <Box className={classes('paymentMethodHeader')} ai="center" gapSize="4px">
                        <span>{t('pushToCardButton')}</span>
                        <AcceptedCardBrandsTooltip acceptedCardBrands={token?.AcceptedCardBrands} position="topRight" />
                      </Box>

                      <div className={classes('feeDisplay')}>
                        <ConvenienceFeeBreakdown connectionMethod="debitcard" />
                      </div>

                      {showFees && hasRestrictionsForCountry && !isPaymentMethodFree('debitcard') && (
                        <Box ai="center" gapSize="4px">
                          <span className={classes('restrictionText')}>
                            {tConvenience('feeWaivedInSome', {
                              regionType,
                              defaultValue: `This fee might be waived in some ${regionType}`,
                            })}
                          </span>

                          <RestrictionsTooltip
                            restrictions={convenienceFees?.Restrictions || []}
                            country={token?.Country}
                            position="right"
                          />
                        </Box>
                      )}
                    </>
                  }
                />
              )}
            </>
          )}

          {isEnabled('email') && (
            <ListItem
              to={{ pathname: '/connect/processor/email', state: { origin: state?.origin } }}
              icon={<Icon icon="interacColor" width="21px" height="21px" color="none" />}
              content={
                <>
                  {flow === Flow.CONNECT ? (
                    <div className={classes('paymentMethodHeader')}>
                      {t('emailButton', {
                        defaultValue: 'Email/Phone Number',
                      })}
                    </div>
                  ) : (
                    <InteracWordmark
                      type={flow === Flow.FUND__SINGLE || flow === Flow.FUND__SCHEDULED ? 'moneyRequest' : 'eTransfer'}
                    />
                  )}

                  <div className={classes('feeDisplay')}>
                    <ConvenienceFeeBreakdown connectionMethod="email" />
                  </div>
                </>
              }
            />
          )}

          {isEnabled('googlepay') && (
            // overlay the actual GooglePayButton on top of the visual card
            <div className={classes('googlePayContainer')}>
              <ListItem
                icon={<Icon icon="googlePay" color="none" width="21px" height="21px" />}
                content={
                  <>
                    <div className={classes('paymentMethodHeader')}>Google Pay</div>
                    <div className={classes('feeDisplay')}>
                      <ConvenienceFeeBreakdown connectionMethod="googlepay" />
                    </div>
                  </>
                }
                className={classes('googlePayListItem')}
              />

              <GooglePayButton className={classes('googlePayButton')} />
            </div>
          )}

          {isEnabled('paypal') && (
            <ListItem
              to="/connect/digital-wallet/paypal"
              icon={<Icon icon="paypal" color="none" width="21px" height="21px" />}
              content={
                <div className={classes('paymentMethodContent')}>
                  <div className={classes('paymentMethodHeader')}>PayPal</div>
                  <div className={classes('feeDisplay')}>
                    <ConvenienceFeeBreakdown connectionMethod="paypal" />
                  </div>
                </div>
              }
            />
          )}

          {isEnabled('venmo') && token?.Country === 'US' && (
            <ListItem
              to="/connect/digital-wallet/venmo"
              icon={<Icon icon="venmo" color="none" width="21px" height="21px" />}
              content={
                <div className={classes('paymentMethodContent')}>
                  <div className={classes('paymentMethodHeader')}>Venmo</div>
                  <div className={classes('feeDisplay')}>
                    <ConvenienceFeeBreakdown connectionMethod="venmo" />
                  </div>
                </div>
              }
            />
          )}
        </div>
      </div>

      <div className={classes('logos')}>
        <Term term={`${t('secure')}`} center />
        <Logos />
      </div>
    </div>
  );
};

export default ConnectionMethod;
